import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2, Users } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Field = ({ label, value, onChange, area, placeholder, type }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">{label}</span>
    {area
      ? <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
      : <input type={type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />}
  </label>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
    <h2 className="font-display font-bold text-navy mb-4">{title}</h2>
    {children}
  </div>
);

export default function MtdManager() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("content");

  useEffect(() => {
    adminApi.mtd.getConfig().then(setForm).catch(() => {});
    adminApi.mtd.leads().then(setLeads).catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBand = (i, patch) => set("bands", form.bands.map((b, bi) => (bi === i ? { ...b, ...patch } : b)));

  const save = async () => {
    setSaving(true);
    try { const updated = await adminApi.mtd.saveConfig(form); setForm(updated); toast({ title: "MTD checker saved" }); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const removeLead = async (id) => {
    await adminApi.mtd.removeLead(id).catch(() => {});
    setLeads((l) => l.filter((x) => x.id !== id));
  };

  if (!form) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="mtd-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">MTD for Income Tax Checker</h1>
          <p className="text-sm text-slate-500">Edit copy, thresholds & dates, the AI summary toggle, and view leads.</p>
        </div>
        {tab === "content" && (
          <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shrink-0" data-testid="mtd-save">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
          </Button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("content")} data-testid="mtd-tab-content" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "content" ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`}>Content & thresholds</button>
        <button onClick={() => setTab("leads")} data-testid="mtd-tab-leads" className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === "leads" ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`}>Leads ({leads.length})</button>
      </div>

      {tab === "content" ? (
        <>
          <Card title="Page copy">
            <div className="space-y-4">
              <Field label="Title" value={form.title} onChange={(v) => set("title", v)} />
              <Field label="Intro" value={form.intro} onChange={(v) => set("intro", v)} area />
              <div className="grid md:grid-cols-2 gap-4 items-start">
                <ImageUpload label="Assessment hub tile image" value={form.hub_tile_image} onChange={(v) => set("hub_tile_image", v)} />
                <Field label="Tile image alt text" value={form.hub_tile_image_alt} onChange={(v) => set("hub_tile_image_alt", v)} placeholder="Describe the image for accessibility/SEO" />
              </div>
              <Field label="Lead capture heading" value={form.lead_heading} onChange={(v) => set("lead_heading", v)} />
              <Field label="Lead capture body" value={form.lead_body} onChange={(v) => set("lead_body", v)} area />
            </div>
          </Card>

          <Card title="Thresholds & mandation dates">
            <p className="text-xs text-slate-500 mb-4">Update these whenever HMRC confirms a change. Bands are checked highest income first. "Min income" is the qualifying-income threshold (£).</p>
            <div className="space-y-3">
              {(form.bands || []).map((b, i) => (
                <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4 relative">
                  <button onClick={() => set("bands", form.bands.filter((_, x) => x !== i))} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`mtd-band-remove-${i}`}><Trash2 size={15} /></button>
                  <div className="grid md:grid-cols-3 gap-3 pr-6">
                    <Field label="Min income (£)" type="number" value={b.min_income} onChange={(v) => setBand(i, { min_income: Number(v) })} />
                    <Field label="From date" value={b.from_date} onChange={(v) => setBand(i, { from_date: v })} placeholder="6 April 2026" />
                    <Field label="Tax year" value={b.tax_year} onChange={(v) => setBand(i, { tax_year: v })} placeholder="2026/27" />
                  </div>
                  <div className="mt-3 pr-6"><Field label="Result message" value={b.label} onChange={(v) => setBand(i, { label: v })} /></div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => set("bands", [...(form.bands || []), { min_income: 0, from_date: "", tax_year: "", label: "" }])} className="rounded-full" data-testid="mtd-band-add"><Plus size={14} className="mr-1" /> Add band</Button>
            </div>
          </Card>

          <Card title="Messaging & AI">
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                <input type="checkbox" checked={!!form.ai_summary_enabled} onChange={(e) => set("ai_summary_enabled", e.target.checked)} className="w-4 h-4 accent-orange" data-testid="mtd-ai-toggle" />
                Enable AI "what this means for you" summary
              </label>
              <Field label="Below-threshold message" value={form.below_threshold_message} onChange={(v) => set("below_threshold_message", v)} area />
              <Field label="Disclaimer" value={form.disclaimer} onChange={(v) => set("disclaimer", v)} area />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="CTA label" value={form.cta_label} onChange={(v) => set("cta_label", v)} />
                <Field label="CTA link" value={form.cta_link} onChange={(v) => set("cta_link", v)} />
              </div>
            </div>
          </Card>
        </>
      ) : (
        <Card title={`Leads (${leads.length})`}>
          {leads.length === 0 ? (
            <p className="text-sm text-slate-400 flex items-center gap-2"><Users size={15} /> No leads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Email</th><th className="py-2 pr-3">Income</th><th className="py-2 pr-3">Result</th><th className="py-2 pr-3">Consent</th><th className="py-2 pr-3">Date</th><th></th>
                </tr></thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50" data-testid={`mtd-lead-${l.id}`}>
                      <td className="py-2 pr-3 text-navy">{l.name || "-"}</td>
                      <td className="py-2 pr-3">{l.email}</td>
                      <td className="py-2 pr-3">£{Number(l.income || 0).toLocaleString()}</td>
                      <td className="py-2 pr-3">{l.mandated ? `From ${l.from_date}` : "Not mandated"}</td>
                      <td className="py-2 pr-3">{l.marketing_consent ? "Yes" : "No"}</td>
                      <td className="py-2 pr-3 text-slate-400">{(l.created_at || "").slice(0, 10)}</td>
                      <td className="py-2"><button onClick={() => removeLead(l.id)} className="text-red-400 hover:text-red-600" data-testid={`mtd-lead-remove-${l.id}`}><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
